import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { WidgetsTypeEnum } from 'src/app/enums/widgets/widgets-type.enum';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { CdkDrag, CdkDragDrop } from '@angular/cdk/drag-drop';
import { WidgetsContainerComponentAbstract } from 'src/app/abstract/widgets/widgets-container-component.abstract';
import { WidgetsService } from 'src/app/services/widgets/widgets.service';
import { WidgetsSectionModel } from 'src/app/models/widgets/widgets-section.model';
import { CommandService } from 'src/app/services/command/command.service';
import PrettySequenceHelper from 'src/app/shared/form-builder-util/pretty-sequence';
import { Logger } from 'src/app/core/logger';
import { WidgetsMetadataService } from 'src/app/services/widgets/widgets-metadata.service';

import { Config } from 'src/app/config';
import { WidgetsNotifyDeleteService } from 'src/app/services/widgets/widgets-notify-delete.service';
import { ViewEncapsulation } from '@angular/core';
import { CommandResequenceQuestionsService } from 'src/app/services/command/command-resequence-questions.service';
import { CommandResequenceSectionsService } from 'src/app/services/command/command-resequence-sections.service';
import { WidgetsSelectionService } from 'src/app/services/widgets/widgets-selection.service';

@Component({
  // eslint-disable-next-line
  selector: '[appWidgetsSection]',
  templateUrl: './widgets-section.component.html',
  styleUrls: ['./widgets-section.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WidgetsSectionComponent
  extends WidgetsContainerComponentAbstract
  implements OnInit
{
  @Input() data: WidgetsSectionModel = new WidgetsSectionModel();
  @Input() dropListIds: string[] = [];
  @Output() dropListIdAdded: EventEmitter<string> = new EventEmitter<string>();
  tempData: WidgetsSectionModel = new WidgetsSectionModel();
  public sequence: string = '';
  isHidden: boolean = false;

  constructor(
    private widgetsService: WidgetsService,
    private selectionService: WidgetsSelectionService,
    private commandService: CommandService,
    private widgetsMetadataService: WidgetsMetadataService,
    private notifyDeleteService: WidgetsNotifyDeleteService,
    private commandResequenceSections: CommandResequenceSectionsService,
    private commandResequenceQuestions: CommandResequenceQuestionsService,
    private logger: Logger
  ) {
    super();
  }

  ngOnInit(): void {
    this.refreshMetadata();

    if (!this.data.seqHidden) {
      this.sequence = this.getPrettySequence(this.data.seq) + '. ';
    }
  }

  public onSequenceHidden() {
    if (!this.data.seqHidden) {
      this.sequence = this.getPrettySequence(this.data.seq) + '. ';
    } else {
      this.sequence = '';
    }
  }

  public getPrettySequence(seq: number[]): string {
    return PrettySequenceHelper.formatSequence(seq);
  }

  onWidgetDroppedToCanvas(
    event: CdkDragDrop<WidgetsAbstract[], WidgetsAbstract[], any>
  ): WidgetsAbstract {
    const temp = this.commandService.execute(this.data, event.item.data);
    this.commandService.prepareForUndo();
    return temp;
  }

  /**
   * @note this is not available inside this method...
   * @param item
   * @returns
   */
  isDroppableWidget(item: CdkDrag<WidgetsAbstract>): boolean {
    // Constraints: Only QUESTIONS and SECTIONS are allowed under a SECTION
    switch (item.data.widgetType) {
      case WidgetsTypeEnum.SECTION:
        if (Config.FEATURES.get("isSectionNestingActive")) {
          return true;
        } else {
          return false;
        }
      case WidgetsTypeEnum.QUESTION:
        return true;
    }

    return false;
  }

  /**
   * We work with a cloned copy of the layout data that we refresh here.
   */
  refreshMetadata() {
    // Use a copy of data for Metadata (we don't want to modify the layout directly)
    this.tempData.cloneFrom(this.data);
    this.tempData.id = this.data.id; // Even use same uuid
    // this.logger.log("refreshMetadata");
  }

  onMaximize(): void {
    this.isMinimized = false;
    this.refreshMetadata();

    // Inform Metadata View (even if it's hidden)
    this.widgetsMetadataService.showMetadata(this.data);
  }

  onMinimize(): void {
    this.isMinimized = true;
    this.refreshMetadata();
  }

  onSectionCollapse(): void {
    this.data.isContainerExpanded = false;
  }

  onSectionExpand(): void {
    this.data.isContainerExpanded = true;
  }

  public onDeleteModal(): void {
    this.notifyDeleteService.notifyDelete(this.data);
  }

  /**
   * @deprecated Use cloneFrom instead
   * @param WidgetsAbstract
   */
  clone(_widget: any): WidgetsAbstract {
    throw new Error('Method not implemented.');
  }

  onWidgetsReordered(siblings: WidgetsAbstract[]) {
    // TODO: When moving from one Section to another, the parentId must be changed
    siblings.forEach((widget) => {
      if (widget.parentId !== this.data.id) {
        // console.log('Question moved to another section. Parent Section id updated.');
        widget.parentId = this.data.id;
      }
    });

    this.commandResequenceSections
      .executeAsync(
        this.widgetsService.getLayoutRoot(),
        this.widgetsService.getLayoutRoot()
      )
      .then((_numSections) => {
        // this.logger.log("onResequence Resequenced "+numSections+" sections.");

        // Resequence Questions
        this.commandResequenceQuestions
          .executeAsync(this.widgetsService.getLayoutRoot(), undefined)
          .then((_numQuestions) => {
            // this.logger.log("onResequence Resequenced "+numQuestions+" questions.");
          })
          .catch((_error) => {
            // this.logger.log("onResequence Questions. "+error);
          });
      })
      .catch((error) => {
        this.logger.log('onResequence Sections. ' + error);
      });
  }

  onSelect(_$event) {
    this.selectionService.toggleSelection(this.data).then(() => {
      this.tempData.isSelected = this.data.isSelected;
      this.widgetsService.setLayoutTouched();
    });
  }
}
